import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EmptyState, Loader } from "@threadws/jute";
import { personasListOptions, tasksListOptions } from "../../api/generated/@tanstack/react-query.gen";
import { CHANNELS } from "../../realtime/ablyClient";
import { useChannel } from "../../realtime/useChannel";
import { today } from "../../utils/calendarDay";
import { groupByPersona, isShown } from "../../utils/taskLabels";
import { invalidateTasks } from "../../utils/taskQueries";
import { useNow } from "../../utils/useNow";
import { TaskRow } from "./TaskRow";

interface TaskListProps {
  /** Only these personas' tasks, plus unassigned ones. Undefined (or empty) for everyone's. */
  personaIds: string[] | undefined;
}

/**
 * Open tasks by persona, plus ones ticked off today. Refreshes whenever a task changes anywhere,
 * from this page, another tab or an agent, through the `sage:tasks` channel.
 */
export function TaskList({ personaIds }: TaskListProps) {
  useNow(); // re-render each minute, so "today" moves on at midnight
  const queryClient = useQueryClient();
  const { data: personas } = useQuery(personasListOptions());
  const { data: tasks, error, isPending } = useQuery({
    ...tasksListOptions({ query: { status: "all", ...(personaIds?.length ? { personaIds } : {}) } }),
    enabled: personaIds !== undefined,
  });
  useChannel(CHANNELS.tasks, () => invalidateTasks(queryClient));

  if (error) return <EmptyState title="Couldn't load your tasks" description={error.message} />;
  if (isPending || !personas) return <Loader label="Loading tasks" />;
  const day = today();
  const shown = tasks.filter((task) => isShown(task, day));
  if (shown.length === 0) return <p className="text-sm text-text-secondary">Nothing to do. Type something above to add tasks.</p>;

  return (
    <div className="flex flex-col gap-5">
      {groupByPersona(shown, personas).map(({ persona, tasks }) => (
        <section key={persona?.id ?? "none"} className="flex flex-col gap-1">
          <h2 className="text-xs font-medium tracking-wide text-text-secondary uppercase">{persona?.name ?? "No persona"}</h2>
          <ul className="flex flex-col">
            {tasks.map((task) => (
              <TaskRow key={task.id} task={task} personas={personas} today={day} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
